import { FC, KeyboardEvent } from 'react';

import { Button, IconButton, Textarea, useFilePicker } from '../../../shared/ui';

interface MeetChatComposerProps {
  draft: string;
  isSendingImage?: boolean;
  disabled?: boolean;
  onDraftChange: (value: string) => void;
  onSend: () => void | Promise<boolean>;
  onImageSelect: (file: File) => void | Promise<boolean>;
}

export const MeetChatComposer: FC<MeetChatComposerProps> = ({
  draft,
  isSendingImage = false,
  disabled = false,
  onDraftChange,
  onSend,
  onImageSelect,
}) => {
  const imagePicker = useFilePicker({ accept: 'image/*', onFiles: ([file]) => void onImageSelect(file) });

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void onSend();
    }
  };

  return (
    <div className="shrink-0 border-t border-line bg-white p-3 pb-[max(12px,env(safe-area-inset-bottom,0px))] sm:p-4">
      <div className="flex items-end gap-2 sm:gap-3">
        {imagePicker.input}
        <IconButton
          label="Выбрать изображение"
          size="lg"
          variant="neutral"
          disabled={disabled || isSendingImage}
          onClick={imagePicker.open}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M12 16V8M8 12H16M5 19H19C20.1 19 21 18.1 21 17V7C21 5.9 20.1 5 19 5H5C3.9 5 3 5.9 3 7V17C3 18.1 3.9 19 5 19Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>
        <Textarea
          aria-label="Сообщение"
          value={draft}
          disabled={disabled}
          rows={1}
          autoGrow
          maxHeight={120}
          placeholder="Введите сообщение..."
          className="no-scrollbar min-h-12 min-w-0 flex-1 py-3"
          onChange={(event) => onDraftChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button
          type="button"
          size="lg"
          variant="dark"
          aria-label="Отправить"
          disabled={disabled || draft.trim().length === 0}
          onClick={() => void onSend()}
        >
          <svg className="sm:hidden" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M5 12H19M19 12L13 6M19 12L13 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="hidden sm:inline">Отправить</span>
        </Button>
      </div>
      {isSendingImage && <p className="mt-2 text-ui-xs text-ink-subtle">Отправляем изображение...</p>}
    </div>
  );
};
